/**
 * API response transformers.
 * Converts snake_case (Python/Django) to camelCase (JavaScript).
 */

/**
 * Fields that contain user data and should NOT have their keys transformed.
 * These are JSON fields where keys come from user input (e.g., CSV column names).
 */
const PRESERVE_KEYS_FIELDS = new Set([
  'variables',
  'dynamicVariables',
  'dynamic_variables',
  'collectedVariables',
  'collected_variables',
  'sourceColumnAliases',
  'source_column_aliases',
  'phoneStatuses',
  'phone_statuses',
  'externalResponse',
  'external_response',
  'metadata',
  'preCallDefaultVariables',
  'pre_call_default_variables',
  'importStats',
  'import_stats',
]);

const SNAKE_TO_CAMEL_KEY_OVERRIDES: Record<string, string> = {
  contact_z_ai_handoff_config: 'contactZAIHandoffConfig',
};

const CAMEL_TO_SNAKE_KEY_OVERRIDES: Record<string, string> = {
  contactZAIHandoffConfig: 'contact_z_ai_handoff_config',
};

/**
 * Convert a snake_case string to camelCase.
 */
export function snakeToCamel(str: string): string {
  if (SNAKE_TO_CAMEL_KEY_OVERRIDES[str]) {
    return SNAKE_TO_CAMEL_KEY_OVERRIDES[str];
  }
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case.
 */
export function camelToSnake(str: string): string {
  if (CAMEL_TO_SNAKE_KEY_OVERRIDES[str]) {
    return CAMEL_TO_SNAKE_KEY_OVERRIDES[str];
  }
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Recursively transform object keys from snake_case to camelCase.
 * @param obj - The object to transform
 * @param preserveKeys - If true, don't transform keys of this object (used for user data fields)
 */
export function transformKeys<T>(obj: unknown, preserveKeys = false): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item, false)) as T;
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      // Transform the key (unless we're inside a preserved field)
      const newKey = preserveKeys ? key : snakeToCamel(key);
      
      // Check if this field's VALUE should have its keys preserved
      const shouldPreserveChildKeys = PRESERVE_KEYS_FIELDS.has(key) || PRESERVE_KEYS_FIELDS.has(newKey);
      
      if (shouldPreserveChildKeys && typeof value === 'object' && value !== null) {
        // Don't transform keys inside this field - it's user data
        result[newKey] = value;
      } else {
        result[newKey] = transformKeys(value, false);
      }
    }
    return result as T;
  }

  return obj as T;
}

/**
 * Recursively transform object keys from camelCase to snake_case.
 * Used for sending data to the API.
 * @param obj - The object to transform
 * @param preserveKeys - If true, don't transform keys of this object (used for user data fields)
 */
export function transformKeysToSnake<T>(obj: unknown, preserveKeys = false): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToSnake(item, false)) as T;
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      // Transform the key (unless we're inside a preserved field)
      const newKey = preserveKeys ? key : camelToSnake(key);
      
      // Check if this field's VALUE should have its keys preserved
      const shouldPreserveChildKeys = PRESERVE_KEYS_FIELDS.has(key) || PRESERVE_KEYS_FIELDS.has(newKey);
      
      if (shouldPreserveChildKeys && typeof value === 'object' && value !== null) {
        // Don't transform keys inside this field - it's user data
        result[newKey] = value;
      } else {
        result[newKey] = transformKeysToSnake(value, false);
      }
    }
    return result as T;
  }

  return obj as T;
}

/**
 * Transform paginated API response.
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function transformPaginatedResponse<T>(response: unknown): PaginatedResponse<T> {
  const data = response as Record<string, unknown>;
  return {
    count: data.count as number,
    next: data.next as string | null,
    previous: data.previous as string | null,
    results: (data.results as unknown[]).map((item) => transformKeys<T>(item)),
  };
}
